// pages/Chat.tsx
import React, { useRef, useEffect, useState, UIEvent, ReactNode } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { z } from "zod";
import { messageValidators } from "@shared/validators/message-validators";
import { useAuthenticatedUserQuery } from "@/api/queries/AuthQueries";
import { useInfiniteScrollMessages } from "@/api/queries/MessageQueries";
import {
    useSendMessageMutation,
    useUpdateMessageMutation,
    useRegenerateMessageMutation,
    useDeleteMessageMutation,
} from "@/api/mutations/MessageMutations";
import { useCreateThreadMutation } from "@/api/mutations/ThreadMutations";
import { useStopStreamMutation } from "@/api/mutations/StreamingMutations";
import { trpc } from "@/lib/trpc";
import { useChatStore } from "@/stores/chat-store";
// import { useArtifactStore } from "@/stores/artifact-store";
import { useOnLLMStream } from "@/api/subscriptions/LLMSubscriptions";
import { UploadedFile } from "@/components/inputs/FileUpload";
import MessageList from "@/components/lists/MessageList";
import ChatInputArea from "@/components/inputs/ChatInputArea";
import ArtifactManager from "@/components/artifacts/ArtifactManager";
import BrandLogo from "@/components/BrandLogo";
import { Info, Globe } from "lucide-react";
import { EditMessageDialog } from "@/components/dialogs/EditMessageDialog";
import { useTranslation } from "react-i18next";

export default function Chat() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const threadId = useParams().threadId;
    const [warningMessage, setWarningMessage] = useState<ReactNode | null>();
    const [isThinking, setIsThinking] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [attachedFiles, setAttachedFiles] = useState<UploadedFile[]>([]);
    const streamingStoppedRef = useRef(false);
    const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
    const [editingMessageContent, setEditingMessageContent] = useState<string>("");

    // Get artifact panel state
    // const { isOpen: isArtifactPanelOpen } = useArtifactStore();

    const { data: user } = useAuthenticatedUserQuery();
    const { filters, sendMessageParams } = useChatStore();

    // Ensure we have a valid threadId or use undefined for the hook
    const { data, error } = useOnLLMStream(threadId || "", setIsThinking, setIsStreaming, streamingStoppedRef);

    const { mutate: createThread, isPending: isPendingCreateThread } = useCreateThreadMutation();
    const { mutate, isPending: isPendingCreateMessage, variables } = useSendMessageMutation();
    const { mutate: updateMessage } = useUpdateMessageMutation();
    const { mutate: regenerateMessage } = useRegenerateMessageMutation();
    const { mutate: deleteMessage } = useDeleteMessageMutation();
    const { mutate: stopStream } = useStopStreamMutation();
    const utils = trpc.useUtils();

    const {
        items: messages,
        bottomRef: fetchMoreTriggerRef,
        isFetchingNextPage: isFetchingNextPageMessage,
        isLoading: isLoadingMessage,
    } = useInfiniteScrollMessages({
        enabled: !!threadId,
        observer: { threshold: 0.8 },
        threadId: threadId ?? "",
        ...filters,
    });

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const newestMessageRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Scroll container event handler
    const handleScroll = (e: UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        const isAtBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 50;
        setAutoScrollEnabled(isAtBottom);
    };

    // Scroll-to-bottom helper
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const scrollToBottom = () => {
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    };

    // Auto-scroll when new messages arrive
    useEffect(() => {
        if (autoScrollEnabled || messages.length < filters.limit) {
            scrollToBottom();
        }
    }, [messages, filters.limit, autoScrollEnabled]);

    // Send a message (or create thread + send)
    const handleSendMessage = async () => {
        if (!textareaRef.current) return;
        const inputValue = textareaRef.current.value.trim();
        if (inputValue === "" && attachedFiles.length === 0) return;

        // Create new abort controller for this operation
        const controller = new AbortController();
        setAbortController(controller);

        const newMessage: z.infer<typeof messageValidators.createMessageSchema> = location.state?.message || {
            threadId: threadId ?? "",
            content: inputValue || (attachedFiles.length > 0 ? `Attached ${attachedFiles.length} file(s)` : ""),
            role: "user",
            contentType: "text",
            metadata: {
                webAgentEnabled: sendMessageParams.web,
                attachments: attachedFiles.map((file) => ({
                    id: file.id,
                    url: file.url,
                    fileName: file.fileName,
                    fileSize: file.fileSize,
                    mimeType: file.mimeType,
                    thumbnailUrl: file.thumbnailUrl,
                    minioKey: file.minioKey,
                    analysis: file.analysis,
                })),
            },
        };

        if (!threadId) {
            createThread(
                { title: inputValue.slice(0, 20) || "New Chat" },
                {
                    onSuccess: (data) => {
                        newMessage.threadId = data.id;
                        navigate(`/chats/${data.id}`, { state: { threadId: data.id, message: newMessage }, replace: true});
                    },
                    onSettled: () => {
                        setAbortController(null);
                    },
                }
            );
            return;
        }

        // Send the message with attachment metadata
        mutate(newMessage, {
            onSuccess: () => {
                // Clear attachments after successful send
                setAttachedFiles([]);
            },
        });

        if (location.state) location.state = null;

        textareaRef.current.value = "";
        textareaRef.current.style.height = "auto";

        // Re-enable auto-scroll and start streaming state
        setAutoScrollEnabled(true);
        setIsThinking(true);

        // Reset streaming stopped flag for new message
        streamingStoppedRef.current = false;

        // Debug: Log the actual message being sent to verify correct prompt
        console.log("🚀 Sending message:", {
            content: inputValue,
            threadId: threadId,
            timestamp: new Date().toISOString(),
            messageObject: newMessage,
        });

        setTimeout(scrollToBottom, 100);
    };

    // Enter key handler
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Textarea auto-resize
    const adjustHeight = () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
    };

    // Handle location state on mount
    useEffect(() => {
        if (location.state && !isLoadingMessage && !isFetchingNextPageMessage) {
            handleSendMessage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, isLoadingMessage]);

    // Handle hash navigation to specific messages
    useEffect(() => {
        if (location.hash && messages.length > 0 && !isLoadingMessage) {
            const messageId = location.hash.substring(1); // Remove the # symbol
            const messageElement = document.getElementById(`message-${messageId}`);

            if (messageElement) {
                // Scroll to the message with some offset for better visibility
                messageElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });

                // Add a temporary highlight effect
                messageElement.classList.add("ring-2", "ring-primary", "ring-opacity-50");
                setTimeout(() => {
                    messageElement.classList.remove("ring-2", "ring-primary", "ring-opacity-50");
                }, 2000);
            }
        }
    }, [location.hash, messages, isLoadingMessage]);

    // Handle recreate message event
    useEffect(() => {
        const handleRecreateMessage = (event: CustomEvent) => {
            const { messageId, threadId: eventThreadId } = event.detail;

            // Only handle if it's for the current thread
            if (eventThreadId !== threadId) return;

            // Find the AI message that will be deleted to get the previous user message
            // IMPORTANT: Do this BEFORE deleting the message to avoid index shifts
            const allMessages = messages.slice().reverse(); // Reverse to get chronological order
            const aiMessageIndex = allMessages.findIndex((msg) => msg.id === messageId);

            if (aiMessageIndex === -1) return;

            // Find the previous user message (the one that triggered this AI response)
            let userMessage: (typeof messages)[0] | null = null;
            for (let i = aiMessageIndex - 1; i >= 0; i--) {
                if (allMessages[i].role === "user") {
                    userMessage = allMessages[i];
                    break;
                }
            }

            if (!userMessage) return;

            // Store the user message data before deleting the AI message
            const userMessageData = {
                content: userMessage.content,
                webAgentEnabled: (userMessage.metadata as any)?.webAgentEnabled || false,
            };

            // Delete the AI message first using the proper mutation
            deleteMessage(messageId);

            // After deletion, regenerate the response
            regenerateMessage({
                threadId: threadId ?? "",
                userMessageContent: userMessageData.content,
                webAgentEnabled: userMessageData.webAgentEnabled,
            });

            // Set states for new generation
            setAutoScrollEnabled(true);
            setIsThinking(true);
            streamingStoppedRef.current = false;

            setTimeout(scrollToBottom, 100);
        };

        window.addEventListener("recreateMessage", handleRecreateMessage as EventListener);

        return () => {
            window.removeEventListener("recreateMessage", handleRecreateMessage as EventListener);
        };
    }, [messages, threadId, regenerateMessage, deleteMessage, setAutoScrollEnabled, setIsThinking, scrollToBottom]);

    // Handle streaming errors
    useEffect(() => {
        if (error) setIsThinking(false);
    }, [error]);

    // Stop current operation
    const handleStopGeneration = () => {
        // Mark streaming as stopped to prevent processing new chunks
        streamingStoppedRef.current = true;

        // Stop streaming states
        setIsThinking(false);
        setIsStreaming(false);

        // Call backend to actually stop the stream
        if (data?.messageId) {
            stopStream({ messageId: data.messageId });
        }

        // Save the current AI message if it exists
        if (data?.messageId && threadId) {
            // Get the current message content from cache
            const cachedMessages = utils.messages.findMany.getInfiniteData({
                ...filters,
                threadId,
            });

            if (cachedMessages) {
                // Find the current streaming message
                const currentMessage = cachedMessages.pages
                    .map((page: any) => page.data)
                    .flat()
                    .find((message: any) => message.id === data.messageId);

                if (currentMessage && currentMessage.content) {
                    // Update the message with current content and mark as complete
                    updateMessage({
                        id: data.messageId,
                        data: {
                            content: currentMessage.content,
                            // Add metadata to indicate it was stopped by user
                            metadata: {
                                ...currentMessage.metadata,
                                stoppedByUser: true,
                                stoppedAt: new Date().toISOString(),
                                // Mark as context-breaking to help AI understand this was interrupted
                                contextBreak: true,
                            },
                        },
                    });
                }
            }
        }

        // Abort any ongoing requests
        if (abortController) {
            abortController.abort();
            setAbortController(null);
        }

        // IMPORTANT: Clear all caches to prevent stale prompts from being reused
        if (threadId) {
            // Cancel any ongoing queries
            utils.messages.findMany.cancel({ threadId, ...filters });

            // Force fresh data fetch after cleanup
            setTimeout(() => {
                utils.messages.findMany.invalidate({ threadId, ...filters });
            }, 200);
        }

        // Clear textarea if needed
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    // Handle edit message
    const handleEditMessage = (messageId: string, currentContent: string) => {
        setEditingMessageId(messageId);
        setEditingMessageContent(currentContent);
    };

    const handleSaveEditedMessage = (newContent: string) => {
        if (!editingMessageId || !threadId) return;

        // Find the message being edited
        const messageIndex = messages.findIndex((msg) => msg.id === editingMessageId);
        if (messageIndex === -1) return;

        // Update the message content
        updateMessage(
            {
                id: editingMessageId,
                data: {
                    content: newContent,
                    deleteAllMessagesThatComeAfter: true,
                },
            },
            {
                onSuccess: () => {
                    utils.messages.findOne.invalidate(data.id);
                    // Get the edited message's metadata for web agent setting
                    const editedMessage = messages[messageIndex];
                    const webAgentEnabled = (editedMessage.metadata as any)?.webAgentEnabled || false;

                    // Regenerate response with the new content
                    regenerateMessage({
                        threadId: threadId,
                        userMessageContent: newContent,
                        webAgentEnabled: webAgentEnabled,
                    });

                    // Set states for new generation
                    setAutoScrollEnabled(true);
                    setIsThinking(true);
                    streamingStoppedRef.current = false;

                    setTimeout(scrollToBottom, 100);
                },
            }
        );

        // Reset editing state
        setEditingMessageId(null);
        setEditingMessageContent("");
    };

    useEffect(() => {
        let messageContent: ReactNode = null;

        // Priority 1: Message limit warning (if over 20 messages)
        if (messages.length > 20 && !!threadId) {
            messageContent = (
                <div className="flex items-center gap-1 text-xs font-semibold">
                    <Info size={14} className="text-blue-600" />
                    <span>You have reached the limit of 20 messages. Messages before the 20th will be ignored</span>
                </div>
            );
        }
        // Priority 2: Web agent active warning (if web search is enabled and no message limit warning)
        else if (sendMessageParams.web) {
            messageContent = (
                <div className="flex items-center gap-1 text-xs font-semibold">
                    <Globe size={14} className="text-orange-600" />
                    <span className="text-orange-700 dark:text-orange-400">
                        Web Agent is active - your message may be used to search the web for relevant information
                    </span>
                </div>
            );
        }

        setWarningMessage(messageContent);

        return () => setWarningMessage(null);
    }, [messages, threadId, sendMessageParams.web]);

    return (
        <div className="flex h-[calc(100vh-64px)] w-full px-4 md:px-0">
            {/* Main chat area - takes remaining space automatically */}
            <div className="flex min-w-0 flex-grow flex-col items-center will-change-auto">
                <div
                    className={cn(
                        "flex h-full w-full flex-col items-center justify-center transition-all duration-300",
                        !!threadId && "flex-grow justify-start",
                        !threadId && "gap-6"
                    )}
                >
                    {!threadId ? (
                        /* Welcome screen */
                        <div className="flex flex-col items-center justify-center gap-4">
                            <div className="w-24">
                                <BrandLogo className="h-24 w-auto" alt="Chat logo" />
                            </div>
                            <h1 className="text-3xl font-bold">
                                {t("chat.welcome")}{" "}
                                <span className="text-secondary">
                                    {user?.user.name?.split(" ")?.[0] || user?.user.name}
                                </span>
                            </h1>
                            {((window as any).__BRANDING__?.motd as string | undefined) && (
                                <p className="text-center italic text-gray-500">
                                    {(window as any).__BRANDING__?.motd as string}
                                </p>
                            )}
                        </div>
                    ) : (
                        /* Chat interface */
                        <div
                            ref={scrollContainerRef}
                            onScroll={handleScroll}
                            className={cn(
                                "w-full flex-grow overflow-y-auto",
                                !threadId && "w-full flex-grow overflow-y-auto"
                            )}
                        >
                            <MessageList
                                isFetchingNextPageMessage={isFetchingNextPageMessage}
                                isLoadingMessage={isLoadingMessage}
                                isThinking={isThinking}
                                isStreaming={isStreaming}
                                messages={messages}
                                fetchMoreTriggerRef={fetchMoreTriggerRef}
                                newestMessageRef={newestMessageRef}
                                newUserMessage={variables}
                                isSendingNewUserMessage={isPendingCreateMessage}
                                streamedMessageId={data?.messageId}
                                streamingAgent={data?.agent}
                                streamingData={data?.content}
                                onEditMessage={handleEditMessage}
                            />
                        </div>
                    )}

                    {/* Chat input */}
                    <ChatInputArea
                        warningMessage={warningMessage || undefined}
                        threadId={threadId}
                        textareaRef={textareaRef}
                        handleInputValueChange={adjustHeight}
                        handleKeyDown={handleKeyDown}
                        handleSendMessage={handleSendMessage}
                        handleStopGeneration={handleStopGeneration}
                        isSending={isThinking || isStreaming || isPendingCreateMessage || isPendingCreateThread}
                        attachedFiles={attachedFiles}
                        onAttachedFilesChange={setAttachedFiles}
                    />
                </div>
            </div>

            {/* Artifact panel */}
            {threadId && <ArtifactManager threadId={threadId} />}

            {/* Edit message dialog */}
            <EditMessageDialog
                open={editingMessageId !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setEditingMessageId(null);
                        setEditingMessageContent("");
                    }
                }}
                messageContent={editingMessageContent}
                onSave={handleSaveEditedMessage}
            />
        </div>
    );
}
